import React from "react";

interface ExpandCollapseBoxOptions {
    expanded: boolean;
    // Re-measures the reserved collapsed footprint when the box's content changes.
    contentDependency: unknown;
}

// Mirrors the SCSS $COMMON-TRANSITION (0.15s); also the fallback duration if transitionend never fires.
const EXPAND_COLLAPSE_TRANSITION_MS = 150;
const TRANSITION_END_FALLBACK_BUFFER_MS = 60;

let boxSize = function (box: HTMLElement) {
    let boxRect = box.getBoundingClientRect();
    return {width: boxRect.width, height: boxRect.height};
};

let pinBoxSize = function (box: HTMLElement, size: {width: number; height: number}) {
    box.style.width = `${size.width}px`;
    box.style.height = `${size.height}px`;
};

let clearBoxSize = function (box: HTMLElement) {
    box.style.width = "";
    box.style.height = "";
    box.style.transition = "";
};

let forceReflow = function (element: HTMLElement) {
    element.getBoundingClientRect();
};

let hideContent = function (content: HTMLElement) {
    content.style.transition = "none";
    content.style.opacity = "0";
};

export let useExpandCollapseBox = function (options: ExpandCollapseBoxOptions) {
    let boxRef = React.useRef<HTMLDivElement>(null);
    let overlayRef = React.useRef<HTMLDivElement>(null);
    let contentRef = React.useRef<HTMLDivElement>(null);
    let [reservedSlotHeight, setReservedSlotHeight] = React.useState<number | null>(null);
    // Lags options.expanded so the card can cross-fade its content and shrink in place before dropping back into its in-flow slot.
    let [renderExpanded, setRenderExpanded] = React.useState(options.expanded);
    let collapsedSizeRef = React.useRef<{width: number; height: number} | null>(null);
    let previousExpandedRef = React.useRef(options.expanded);
    let expandRequestedRef = React.useRef(false);
    // Set after a collapse shrink so the renderExpanded effect fades collapsed content back in once the in-flow DOM commits.
    let collapseFadeInRequestedRef = React.useRef(false);
    // Capture the collapsed height so the in-flow slot keeps reserving the footprint once the box floats out of flow; also the FLIP start/end size.
    React.useLayoutEffect(() => {
        if (!renderExpanded && boxRef.current) {
            let collapsedRect = boxRef.current.getBoundingClientRect();
            setReservedSlotHeight(collapsedRect.height);
            collapsedSizeRef.current = {width: collapsedRect.width, height: collapsedRect.height};
        }
    }, [renderExpanded, options.contentDependency]);
    // Run a transition, then clear the inline pins once a watched property settles (or after a fallback if transitionend never fires).
    let runTransition = function (element: HTMLElement, watchedProperties: string[], onSettled: () => void) {
        let settled = false;
        let settle = function () {
            if (settled) {
                return;
            }
            settled = true;
            element.removeEventListener("transitionend", handleTransitionEnd);
            window.clearTimeout(fallbackTimer);
            onSettled();
        };
        let handleTransitionEnd = function (transitionEvent: TransitionEvent) {
            if (transitionEvent.target === element && watchedProperties.indexOf(transitionEvent.propertyName) !== -1) {
                settle();
            }
        };
        element.addEventListener("transitionend", handleTransitionEnd);
        let fallbackTimer = window.setTimeout(settle, EXPAND_COLLAPSE_TRANSITION_MS + TRANSITION_END_FALLBACK_BUFFER_MS);
    };
    // Fade box content from hidden up to visible; used on expand (during the grow) and after a collapse shrink settles.
    let fadeContentIn = function (content: HTMLDivElement) {
        hideContent(content);
        forceReflow(content);
        window.requestAnimationFrame(function () {
            content.style.transition = "";
            content.style.opacity = "1";
        });
        runTransition(content, ["opacity"], function () {
            content.style.opacity = "";
            content.style.transition = "";
        });
    };
    // Grow the box from its collapsed footprint to the natural floating size while fading the overlay and content in.
    let animateExpand = function (box: HTMLDivElement) {
        let expandedSize = boxSize(box);
        let collapsedSize = collapsedSizeRef.current || expandedSize;
        let overlay = overlayRef.current;
        let content = contentRef.current;
        box.style.transition = "none";
        pinBoxSize(box, collapsedSize);
        if (overlay) {
            overlay.style.transition = "none";
            overlay.style.opacity = "0";
        }
        // Content was faded out before the expanded DOM committed; hold it hidden this frame so it fades back in as the box grows.
        if (content) {
            hideContent(content);
        }
        forceReflow(box);
        window.requestAnimationFrame(function () {
            box.style.transition = "";
            pinBoxSize(box, expandedSize);
            if (overlay) {
                overlay.style.transition = "";
                overlay.style.opacity = "1";
            }
            if (content) {
                content.style.transition = "";
                content.style.opacity = "1";
            }
        });
        runTransition(box, ["width", "height"], function () {
            clearBoxSize(box);
            if (overlay) {
                overlay.style.opacity = "";
            }
            if (content) {
                content.style.opacity = "";
                content.style.transition = "";
            }
        });
    };
    // Shrink the box from its floating size back to the collapsed footprint while fading the overlay and content out, then hand off to the renderExpanded effect to drop it into the in-flow slot and fade the collapsed content back in.
    let animateCollapse = function (box: HTMLDivElement) {
        let expandedSize = boxSize(box);
        let collapsedSize = collapsedSizeRef.current || expandedSize;
        let overlay = overlayRef.current;
        let content = contentRef.current;
        box.style.transition = "none";
        pinBoxSize(box, expandedSize);
        if (overlay) {
            overlay.style.transition = "none";
            overlay.style.opacity = "1";
        }
        if (content) {
            content.style.transition = "";
            content.style.opacity = "1";
        }
        forceReflow(box);
        window.requestAnimationFrame(function () {
            box.style.transition = "";
            pinBoxSize(box, collapsedSize);
            if (overlay) {
                overlay.style.transition = "";
                overlay.style.opacity = "0";
            }
            if (content) {
                content.style.opacity = "0";
            }
        });
        runTransition(box, ["width", "height"], function () {
            // Keep the content hidden across the swap back to the collapsed in-flow DOM so the effect can fade it in cleanly.
            if (content) {
                hideContent(content);
            }
            collapseFadeInRequestedRef.current = true;
            setRenderExpanded(false);
        });
    };
    // Drive the FLIP when options.expanded flips: expand fades the collapsed content out, commits the expanded DOM so its natural size can be measured, then grows; collapse can run immediately since the expanded DOM is still on screen.
    React.useLayoutEffect(() => {
        let wasExpanded = previousExpandedRef.current;
        previousExpandedRef.current = options.expanded;
        if (wasExpanded === options.expanded) {
            return;
        }
        if (options.expanded) {
            if (boxRef.current) {
                collapsedSizeRef.current = boxSize(boxRef.current);
            }
            let content = contentRef.current;
            if (content) {
                content.style.transition = "";
                content.getBoundingClientRect();
                window.requestAnimationFrame(function () {
                    content.style.opacity = "0";
                });
                runTransition(content, ["opacity"], function () {
                    expandRequestedRef.current = true;
                    setRenderExpanded(true);
                });
            } else {
                expandRequestedRef.current = true;
                setRenderExpanded(true);
            }
        } else if (boxRef.current) {
            animateCollapse(boxRef.current);
        }
    }, [options.expanded]);
    React.useLayoutEffect(() => {
        if (expandRequestedRef.current && renderExpanded && boxRef.current) {
            expandRequestedRef.current = false;
            animateExpand(boxRef.current);
        } else if (collapseFadeInRequestedRef.current && !renderExpanded) {
            collapseFadeInRequestedRef.current = false;
            if (boxRef.current) {
                clearBoxSize(boxRef.current);
            }
            if (contentRef.current) {
                fadeContentIn(contentRef.current);
            }
        }
    }, [renderExpanded]);
    return {boxRef, overlayRef, contentRef, renderExpanded, reservedSlotHeight};
};
